import React, { useMemo, useEffect } from 'react';
import qs from 'qs';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useLocation, useHistory } from 'react-router';
import { logPostHogEvent, getDealsString, getExtraDeals } from '@utils/index';
import { DealPostType } from '@types';
import { useGetPromotions } from '@hooks/usePromotions';
import { useSnackbar } from '@hooks/useSnackbar';

const FilterByPromotion = ({
  deals,
  promotionFilter,
  setPromotionFilter
}: {
  deals: DealPostType[];
  promotionFilter: string;
  setPromotionFilter: (promotion: string) => void;
}) => {
  const location = useLocation();
  const history = useHistory();
  const { showMessage } = useSnackbar();
  const { data: promotionAPIData } = useGetPromotions();
  const promotionData = useMemo(
    () => promotionAPIData || [],
    [promotionAPIData]
  );
  const promotionDeals = useMemo(() => getExtraDeals(deals) || [], [deals]);

  useEffect(() => {
    if (
      location?.search?.includes('promotionId=') &&
      promotionFilter === 'all' &&
      promotionData.length > 0
    ) {
      const search = qs.parse(location.search, {
        ignoreQueryPrefix: true
      }) as {
        promotionId: string;
      };
      const promotionId = Number(search.promotionId);

      // get the promotion url from the id
      const promotion = promotionData.find((promo) => promo.id === promotionId);
      if (promotion) {
        setPromotionFilter(promotion.extraDealLink);

        showMessage('Filtering by selected promotion...');

        // remove all paramaters from the url
        history.push({
          search: ''
        });
      }
    }
  }, [location.search, promotionData, history, showMessage]);

  return (
    <FormControl fullWidth size="small">
      <InputLabel id="filter-by-promotion-label">
        Filter By Promotion
      </InputLabel>
      <Select
        labelId="filter-by-promotion-label"
        id="filter-by-promotion"
        value={promotionFilter}
        label="Filter By Promotion"
        onChange={(e) => {
          setPromotionFilter(e.target.value as string);
          logPostHogEvent('page-deal-filter-by-promotion', {
            value: e.target.value
          });
        }}
      >
        <MenuItem value="all">All Promotions</MenuItem>
        {promotionDeals.map((extraDeal) => (
          <MenuItem
            key={extraDeal.value}
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.2
            }}
            value={extraDeal.value}
          >
            {`${extraDeal.amount} ${getDealsString(extraDeal.amount)} — ${
              extraDeal.label
            }`}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default FilterByPromotion;
